import React, { useMemo } from 'react';

import { ErrorMessage, FormAutocomplete, FormCheckbox, FormInput, FormTextarea, InputWrapper } from './input-styled-components';
import { ToggleSelect } from './ToggleSelect/ToggleSelect';

export enum InputType {
  TEXT = 'text',
  PASSWORD = 'password',
  EMAIL = 'email',
  TEL = 'tel',
  NUMBER = 'number',
  TEXTAREA = 'textarea',
  CHECKBOX = 'checkbox',
  TOGGLE = 'toggle',
  AUTOCOMPLETE = 'autocomplete',
}

const genericInputTypes: InputType[] = [
  InputType.TEXT,
  InputType.PASSWORD,
  InputType.TEL,
  InputType.EMAIL,
  InputType.NUMBER,
];

const specialInputTypes: Partial<Record<InputType, React.ComponentType<any>>> = {
  [InputType.TEXTAREA]: FormTextarea,
  [InputType.TOGGLE]: ToggleSelect,
  [InputType.CHECKBOX]: FormCheckbox,
  [InputType.AUTOCOMPLETE]: FormAutocomplete,
};

const getComponentForInputType = (inputType: InputType): React.ComponentType<any> => {
  const isGenericInput = genericInputTypes.includes(inputType);

  if (isGenericInput) {
    return FormInput;
  }

  const SpecialInput = specialInputTypes[inputType];

  if (!SpecialInput) {
    throw new Error(`Input type ${inputType} is not supported`);
  }

  return SpecialInput;
};

const reactComponentInputs = [
  InputType.TOGGLE,
];

interface InputProps {
  type: InputType;
  errorMessage?: string;
  isSensitive?: boolean;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

export const Input = ({
  type,
  errorMessage,
  isSensitive = true,
  className,
  ...props
}: InputProps) => {
  const InputComponent = useMemo(() => getComponentForInputType(type), [type]);

  // styled-components use $transient props, but we also have React components, so we need to handle both cases
  const propsToPass = useMemo(() => {
    const isInvalid = Boolean(errorMessage);
    const inputIsSensitive = isSensitive || type === InputType.PASSWORD;

    const classNameToPass = (() => {
      if (!inputIsSensitive) {
        return className;
      }

      if (!className) {
        return 'sensitive-data';
      }

      return `${className} sensitive-data`;
    })();

    if (reactComponentInputs.includes(type)) {
      return {
        ...props,
        className: classNameToPass,
        isSensitive: inputIsSensitive,
        isInvalid,
      };
    }

    return { ...props, className: classNameToPass, $isInvalid: isInvalid };
  }, [props, errorMessage, type, isSensitive, className]);

  return (
    <InputWrapper>
      <InputComponent type={type} {...propsToPass} />
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputWrapper>
  );
};
